/**
 * Device Verification Modal
 * Handles device verification for enhanced security
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Smartphone, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { getDeviceInfo, getDeviceFingerprint } from '@/lib/deviceUtils';

interface DeviceVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerificationComplete: () => void;
  deviceInfo?: any;
}

const DeviceVerificationModal: React.FC<DeviceVerificationModalProps> = ({
  isOpen,
  onClose,
  onVerificationComplete,
  deviceInfo
}) => {
  const { toast } = useToast();
  const { user, deviceSession } = useAuth();
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [step, setStep] = useState<'info' | 'verification' | 'success'>('info');
  const [deviceFingerprint, setDeviceFingerprint] = useState('');
  const [currentDeviceInfo, setCurrentDeviceInfo] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      initializeDeviceInfo();
    }
  }, [isOpen]);

  const initializeDeviceInfo = async () => {
    try {
      const info = deviceInfo || getDeviceInfo();
      const fingerprint = await getDeviceFingerprint();
      
      setCurrentDeviceInfo(info);
      setDeviceFingerprint(fingerprint);
      setStep('info');
    } catch (error) {
      console.error('Error initializing device info:', error);
    }
  };

  const handleStartVerification = () => {
    setStep('verification');
    // In a real implementation, you would send a verification code via email/SMS
    // For demo purposes, we'll use a mock code
    toast({
      title: "Verification Code Sent",
      description: "A verification code has been sent to your email address.",
      variant: "success",
    });
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      toast({
        title: "Verification Code Required",
        description: "Please enter the verification code sent to your device.",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);

    try {
      // In a real implementation, you would verify the code with your backend
      // For demo purposes, we'll accept any 6-digit code
      if (verificationCode.length === 6) {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setStep('success');
        
        toast({
          title: "Device Verified",
          description: "Your device has been successfully verified and registered.",
          variant: "success",
        });

        // Wait a moment then complete
        setTimeout(() => {
          onVerificationComplete();
        }, 1500);
      } else {
        throw new Error('Invalid verification code');
      }
    } catch (error) {
      toast({
        title: "Verification Failed",
        description: "Invalid verification code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSkipVerification = () => {
    toast({
      title: "Verification Skipped",
      description: "You can verify your device later in settings.",
      variant: "default",
    });
    onVerificationComplete();
  };

  const formatDeviceInfo = (info: any) => {
    return {
      'Device Type': info.platform || 'Unknown',
      'Browser': info.userAgent?.split(' ').slice(-2).join(' ') || 'Unknown',
      'Screen Size': info.screenSize || 'Unknown',
      'Language': info.language || 'Unknown',
      'Timezone': info.timezone || 'Unknown',
    };
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: "spring", duration: 0.3 }}
          className="w-full max-w-md"
        >
          <Card className="relative">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-between items-start">
                <div className="flex-1" />
                <div className="flex items-center gap-2">
                  <Shield className="h-6 w-6 text-blue-600" />
                  <CardTitle className="text-lg">Device Verification</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Verify your device for enhanced security
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {step === 'info' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Smartphone className="h-5 w-5 text-blue-600" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">New Device Detected</p>
                      <p className="text-blue-700">We need to verify this device for security purposes.</p>
                    </div>
                  </div>

                  {currentDeviceInfo && (
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Device Information</Label>
                      <div className="space-y-2 text-sm">
                        {Object.entries(formatDeviceInfo(currentDeviceInfo)).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-600">{key}:</span>
                            <span className="font-medium">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={handleSkipVerification}
                      className="flex-1"
                    >
                      Skip for Now
                    </Button>
                    <Button
                      onClick={handleStartVerification}
                      className="flex-1"
                    >
                      Verify Device
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 'verification' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div className="text-center space-y-2">
                    <AlertTriangle className="h-8 w-8 text-orange-500 mx-auto" />
                    <p className="text-sm text-gray-600">
                      Enter the 6-digit verification code sent to your email address.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="verification-code">Verification Code</Label>
                    <Input
                      id="verification-code"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center text-lg font-mono tracking-widest"
                      maxLength={6}
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setStep('info')}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleVerifyCode}
                      disabled={isVerifying || verificationCode.length !== 6}
                      className="flex-1"
                    >
                      {isVerifying ? 'Verifying...' : 'Verify Code'}
                    </Button>
                  </div>

                  <div className="text-center">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        // In real implementation, resend code
                        toast({
                          title: "Code Resent",
                          description: "A new verification code has been sent.",
                          variant: "success",
                        });
                      }}
                    >
                      Resend Code
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center space-y-4"
                >
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                  <div>
                    <h3 className="text-lg font-semibold text-green-900">Device Verified!</h3>
                    <p className="text-sm text-green-700">
                      Your device has been successfully registered and verified.
                    </p>
                  </div>
                  
                  <div className="pt-4">
                    <Button
                      onClick={onVerificationComplete}
                      className="w-full"
                    >
                      Continue
                    </Button>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DeviceVerificationModal;
